import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, X, CheckCircle, AlertCircle, Music } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllCategories,
  addSongsToCategory,
} from "../../redux/reduxSlices/manageCategorySlice";
import toast from "react-hot-toast";

const AddSongs = () => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm();

  const [files, setFiles] = useState([]);
  const [songGenres, setSongGenres] = useState({});
  const [languageList, setLanguageList] = useState([]);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [otherLanguage, setOtherLanguage] = useState("");
  const [uploading, setUploading] = useState(false);

  const { getAllCategoriesResponse } = useSelector(
    (state) => state.manageCategory,
  );

  useEffect(() => {
    dispatch(getAllCategories());
  }, []);

  useEffect(() => {
    if (watch("name")) {
      let list = [];
      getAllCategoriesResponse?.data?.forEach((category) => {
        if (category.name === watch("name")) {
          category.songs?.forEach((song) => {
            list.push(song.subCategory);
          });
        }
      });
      setLanguageList(list);
      setValue("language", "");
      setIsOtherSelected(false);
      setOtherLanguage("");
    }
  }, [watch("name"), getAllCategoriesResponse]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const newGenres = {};
    selectedFiles.forEach((file, index) => {
      const fileId = `${Date.now()}_${index}`;
      newGenres[fileId] = {
        file: file,
        genre: "",
        tempId: fileId,
      };
    });

    setFiles((prev) => [...prev, ...selectedFiles]);
    setSongGenres((prev) => ({
      ...prev,
      ...newGenres,
    }));
  };

  const removeFile = (index) => {
    const fileToRemove = files[index];
    const genreEntry = Object.entries(songGenres).find(
      ([_, value]) => value.file === fileToRemove,
    );

    setFiles((prev) => prev.filter((_, i) => i !== index));

    if (genreEntry) {
      const newSongGenres = { ...songGenres };
      delete newSongGenres[genreEntry[0]];
      setSongGenres(newSongGenres);
    }
  };

  const updateSongGenre = (fileId, genre) => {
    setSongGenres((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        genre: genre,
      },
    }));
  };

  const onSubmit = async (data) => {
    // Validate all songs have genres
    let hasMissingGenre = false;
    Object.values(songGenres).forEach((song) => {
      if (!song.genre || song.genre.trim() === "") {
        hasMissingGenre = true;
      }
    });

    if (hasMissingGenre) {
      toast.error("Please enter genre for all songs");
      return;
    }

    // Get final language
    let finalLanguage = data.language;
    if (data.language === "other") {
      if (!otherLanguage || otherLanguage.trim() === "") {
        toast.error("Please enter a custom language name");
        return;
      }
      finalLanguage = otherLanguage.trim();
    }

    if (!finalLanguage) {
      toast.error("Please select or enter a language");
      return;
    }

    if (files.length === 0) {
      toast.error("Please select at least one song file");
      return;
    }

    setUploading(true);

    // Prepare songs array for Redux
    const songsArray = Object.values(songGenres).map((song) => ({
      file: song.file,
      genre: song.genre,
    }));

    try {
      const response = await dispatch(
        addSongsToCategory({
          name: data.name,
          language: finalLanguage,
          songs: songsArray,
        }),
      ).unwrap();

      if (response.success) {
        toast.success(response.message || "Songs added successfully!");
        // Reset form
        reset({
          name: "",
          language: "",
        });
        setFiles([]);
        setSongGenres({});
        setIsOtherSelected(false);
        setOtherLanguage("");
        // Refresh categories to show updated song count
        dispatch(getAllCategories());
      } else {
        toast.error(response.message || "Failed to add songs");
      }
    } catch (error) {
      toast.error(error?.message || "An error occurred while adding songs");
    } finally {
      setUploading(false);
    }
  };

  const watchedLanguage = watch("language");

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Add Songs to Existing Category
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Category Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Name *
          </label>
          <select
            {...register("name", { required: "Category name is required" })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Select Category</option>
            {getAllCategoriesResponse?.data?.length > 0 ? (
              getAllCategoriesResponse.data.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))
            ) : (
              <option value="">No categories available</option>
            )}
          </select>
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Language */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language *
          </label>
          <select
            {...register("language", {
              required: "Language is required",
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            onChange={(e) => {
              setIsOtherSelected(e.target.value === "other");
              if (e.target.value !== "other") {
                setOtherLanguage("");
              }
            }}
          >
            <option value="">Select Language</option>
            {languageList.length > 0 ? (
              languageList.map((lang, index) => (
                <option key={index} value={lang}>
                  {lang}
                </option>
              ))
            ) : (
              <option value="">No languages available</option>
            )}
            <option value="other">Other</option>
          </select>

          {isOtherSelected && (
            <input
              type="text"
              placeholder="Please specify your language"
              value={otherLanguage}
              onChange={(e) => setOtherLanguage(e.target.value)}
              className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          )}

          {errors.language && !isOtherSelected && (
            <p className="mt-1 text-sm text-red-600">
              {errors.language.message}
            </p>
          )}
        </div>

        {/* File Upload with Genre Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload New Songs (MP3 files) *
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-orange-500 transition-colors">
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none">
                  <span>Upload files</span>
                  <input
                    type="file"
                    multiple
                    accept="audio/mp3,audio/mpeg"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">MP3 up to 10MB each</p>
            </div>
          </div>

          {/* File List with Genre Input */}
          {files.length > 0 && (
            <div className="mt-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Selected Files ({files.length}):
              </h4>
              <div className="max-h-60 overflow-y-auto space-y-3">
                {files.map((file, index) => {
                  const genreEntry = Object.entries(songGenres).find(
                    ([_, value]) => value.file === file,
                  );
                  const fileId = genreEntry?.[0];
                  const currentGenre = genreEntry?.[1]?.genre || "";

                  return (
                    <div
                      key={index}
                      className="bg-gray-50 p-3 rounded-lg space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          <Music size={16} className="text-gray-500" />
                          <span className="text-sm text-gray-600 truncate">
                            {file.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Genre *
                        </label>
                        <input
                          type="text"
                          value={currentGenre}
                          onChange={(e) =>
                            updateSongGenre(fileId, e.target.value)
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="e.g., Rock, Pop, Classical, Folk"
                          required
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={uploading || files.length === 0}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? "Adding Songs..." : "Add Songs"}
        </button>
      </form>
    </div>
  );
};

export default AddSongs;
